import { useMemo, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import tinycolor from "tinycolor2";
import {
  COLOUR_FALLBACK,
  isValidCssColor,
} from "./color-picker/utils/colorFormatUtils";
import React from "react";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";

interface ColorSwatchProps {
  color?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
  showBorder?: boolean;
  backgroundColor?: string;
  contrastThreshold?: number;
  "data-testid"?: string;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
  color,
  size = "sm",
  className,
  showBorder = true,
  backgroundColor = "#131519", // Default dark background
  contrastThreshold = 3,
  "data-testid": dataTestId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const cssElementLookup = useCodebaseStore((state) => state.cssElementLookup);
  const isCanvasDarkMode = useDarkModeStore((state) => state.isCanvasDarkMode);

  // Parse the color to get the proper hex value or gradient
  const swatchColor = useMemo(() => {
    if (!color) return COLOUR_FALLBACK;

    // Check if it's a gradient
    if (color.startsWith("linear-gradient")) {
      return color;
    }

    // If it's already a hex value, use it directly
    if (color.startsWith("#")) {
      return color;
    }

    // Otherwise, try to parse it with TinyColor if it's a valid color
    if (isValidCssColor(color)) {
      const parsed = tinycolor(color);
      return parsed.toHexString();
    }

    return color;
  }, [color]);

  // Check if the swatch needs a border due to low contrast
  const needsBorder = useMemo(() => {
    if (!showBorder) return false;

    const contrast = tinycolor.readability(swatchColor, backgroundColor);
    return contrast < contrastThreshold;
  }, [swatchColor, backgroundColor, contrastThreshold, showBorder]);

  // Create shadow DOM and update styles
  useEffect(() => {
    if (!containerRef.current) return;

    // Create shadow root if it doesn't exist
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({
        mode: "open",
      });
    }

    // Generate CSS variables from cssElementLookup
    const cssVariables: string[] = [];

    Object.values(cssElementLookup).forEach((element: any) => {
      if (!element.styles) return;

      // Check if this is a dark mode selector
      const isDarkModeSelector =
        element.selector === ".dark" ||
        element.selector === "@media (prefers-color-scheme: dark)" ||
        element.selector?.includes("prefers-color-scheme: dark");

      // Skip if dark mode mismatch
      if (isDarkModeSelector !== isCanvasDarkMode) {
        // For light mode, only use :root and non-dark selectors
        if (!isCanvasDarkMode && element.selector !== ":root") return;
        // For dark mode, only use dark selectors
        if (isCanvasDarkMode && !isDarkModeSelector) return;
      }

      // Extract only CSS variables (starting with --)
      Object.entries(element.styles).forEach(([key, value]) => {
        if (key.startsWith("--")) {
          cssVariables.push(`${key}: ${value};`);
        }
      });
    });

    // Create the shadow DOM content
    const shadowContent = `
      <style>
        :host {
          display: inline-block;
        }
        .swatch {
          ${cssVariables.join("\n          ")}
          border-radius: 2px;
          box-shadow: inset 0 1px 2px 0 rgb(0 0 0 / 0.05);
          cursor: pointer;
          border-style: solid;
          border-width: 0.5px;
        }
        .sm { min-width: 12px; min-height: 12px; max-width: 12px; max-height: 12px; }
        .md { min-width: 16px; min-height: 16px; max-width: 16px; max-height: 16px; }
        .lg { min-width: 24px; min-height: 24px; max-width: 24px; max-height: 24px; }
      </style>
      <div class="swatch ${size}" style="${getSwatchStyle()}"></div>
    `;

    function getSwatchStyle() {
      const isGradient = swatchColor.startsWith("linear-gradient");
      const backgroundStyle = isGradient
        ? `background: ${swatchColor}`
        : `background-color: ${swatchColor}`;

      const borderColor = needsBorder
        ? "border-color: rgba(255, 255, 255, 0.3)"
        : isGradient
          ? "border-color: transparent"
          : `border-color: ${swatchColor}`;

      return `${backgroundStyle}; ${borderColor}`;
    }

    shadowRootRef.current.innerHTML = shadowContent;
  }, [cssElementLookup, isCanvasDarkMode, swatchColor, needsBorder, size]);

  return (
    <div
      ref={containerRef}
      className={cn("inline-block", className)}
      data-testid={dataTestId}
    />
  );
};

export default React.memo(ColorSwatch);
